import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { agencySessionKey, canWriteAgencyCommerce, subscribeAgencySession } from "../../../utils/agencySession.ts";
import type { AccountOrder } from "./accountOrderService.ts";
import { catalogDraftOptions, readCatalogDestination, selectCatalogDestination, selectedCatalogOrderTitle, selectedCatalogAgency } from "./catalogOrderService.ts";
import { t, useStoreLanguage } from "../../../features/store-language/i18n.ts";
import AppFeedback from "../../shared/AppFeedback.tsx";
import StoreOrderDialog from "../shared/StoreOrderDialog.tsx";
import type { CatalogOrderOption } from "./catalogOrderService.ts";
import { hasPendingCatalogOrder, latestCatalogDraft, pendingCatalogOrderId, resetCatalogOrder, selectedCatalogOrder, writeCatalogOrder } from "./catalogOrderService.ts";

export function useCatalogOrder() {
  useStoreLanguage();
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  const [request, setRequest] = useState<
    { kind: "add"; variantId: number; quantity: number; session: string | null }
    | { kind: "select"; session: string | null }
  >();
  const [latest, setLatest] = useState<CatalogOrderOption | null>(null);
  const [drafts, setDrafts] = useState<CatalogOrderOption[]>([]);
  const [mode, setMode] = useState<"latest" | "new">("new");
  const [title, setTitle] = useState("");
  const [salesAgencyId, setSalesAgencyId] = useState<number>();
  const [retry, setRetry] = useState(false);
  const [order, setOrder] = useState<AccountOrder | null>(null);
  const [readFailed, setReadFailed] = useState(false);
  const [activeVariant, setActiveVariant] = useState<number>();
  const [, refreshDestination] = useState(0);
  const locked = useRef(false);
  const mounted = useRef(true);
  useEffect(function () { mounted.current = true; return function () { mounted.current = false; }; }, []);

  async function refreshOrder() {
    const session = agencySessionKey();
    try {
      const value = await readCatalogDestination();
      if (mounted.current && session === agencySessionKey()) { setOrder(value); setReadFailed(false); }
    } catch (error) {
      if (mounted.current && session === agencySessionKey()) { setOrder(null); setReadFailed(true); }
      AppFeedback.httpError(error);
    }
  }

  useEffect(function () {
    async function load() {
      if (locked.current) return;
      locked.current = true;
      setPending(true);
      await refreshOrder();
      locked.current = false;
      if (mounted.current) setPending(false);
    }
    void load();
    const unsubscribe = subscribeAgencySession(function () {
      resetCatalogOrder();
      setOrder(null);
      setRequest(undefined);
      setReadFailed(false);
      refreshDestination(function (value) { return value + 1; });
    });
    return unsubscribe;
  }, []);

  async function applyWriteResult(value: AccountOrder | null, session: string | null) {
    if (!mounted.current || session !== agencySessionKey()) return;
    if (value) {
      setOrder(value);
      setReadFailed(false);
    } else {
      // Creation returns only the ID; editing already returns the committed order.
      await refreshOrder();
    }
  }

  async function refreshRejectedWrite(error: unknown) {
    const status = (error as { response?: { status?: number } }).response?.status;
    if (selectedCatalogOrder() && (status === 400 || status === 409)) {
      // Fetch the current version for review, never replay the quantity delta.
      await refreshOrder();
    }
  }

  async function submit() {
    if (locked.current || !request) return;
    locked.current = true;
    setPending(true);
    let previousOrderId: number | undefined;
    try {
      if (request.session !== agencySessionKey()) throw new Error(t("اجازهٔ تغییر سفارش را ندارید."));
      if (request.kind === "select") {
        if (mode === "latest" && !latest) throw new Error(t("انتخاب سفارش جاری"));
        const selected = await selectCatalogDestination(mode === "latest" ? latest?.id : undefined, title, salesAgencyId);
        if (mounted.current && request.session === agencySessionKey()) {
          setOrder(selected);
          setReadFailed(false);
          setRequest(undefined);
          refreshDestination(function (value) { return value + 1; });
        }
        return;
      }
      previousOrderId = retry ? pendingCatalogOrderId() : undefined;
      const updatedOrder = await writeCatalogOrder(request.variantId, request.quantity, mode === "latest" ? latest?.id : undefined, title, salesAgencyId);
      await applyWriteResult(updatedOrder, request.session);
      if (mounted.current && request.session === agencySessionKey()) {
        setRequest(undefined);
      }
    } catch (error) {
      AppFeedback.httpError(error);
      if (mounted.current && request.session === agencySessionKey() && request.kind === "add") {
        const stillPending = hasPendingCatalogOrder();
        setRetry(stillPending);
        // A rejected retry may follow a successful write whose response was lost.
        // Open the real order for review instead of turning retry into a new add.
        if (previousOrderId && !stillPending) {
          setRequest(undefined);
          navigate(`/account/orders/${previousOrderId}`);
        } else if (!stillPending) {
          await refreshRejectedWrite(error);
        }
      }
    } finally { locked.current = false; if (mounted.current) setPending(false); }
  }

  async function add(variantId: number, quantity: number) {
    if (locked.current || request) return;
    locked.current = true;
    setPending(true);
    const session = agencySessionKey();
    setActiveVariant(variantId);
    try {
      if (!canWriteAgencyCommerce()) throw new Error(t("اجازهٔ تغییر سفارش را ندارید."));
      const uncertain = hasPendingCatalogOrder();
      const id = selectedCatalogOrder();
      const destinationTitle = selectedCatalogOrderTitle();
      if ((id || destinationTitle) && !uncertain) {
        const updatedOrder = await writeCatalogOrder(variantId, quantity, id, destinationTitle, selectedCatalogAgency());
        await applyWriteResult(updatedOrder, session);
        return;
      }
      const draft = uncertain ? null : await latestCatalogDraft();
      const options = uncertain ? [] : await catalogDraftOptions();
      if (!mounted.current || session !== agencySessionKey()) return;
      setDrafts(options);
      setLatest(options.find(function (option) { return option.id === draft?.id; }) || options[0] || null);
      setMode(options.length ? "latest" : "new");
      setTitle("");
      setSalesAgencyId(undefined);
      setRetry(uncertain);
      setRequest({ kind: "add", variantId, quantity, session });
    } catch (error) {
      AppFeedback.httpError(error);
      if (mounted.current && session === agencySessionKey()) {
        if (!selectedCatalogOrder()) setOrder(null);
        else if (!hasPendingCatalogOrder()) await refreshRejectedWrite(error);
      }
    }
    finally { locked.current = false; if (mounted.current) setPending(false); }
  }

  async function changeOrder() {
    if (locked.current || request) return;
    locked.current = true;
    setPending(true);
    setActiveVariant(undefined);
    const session = agencySessionKey();
    try {
      if (!canWriteAgencyCommerce()) throw new Error(t("اجازهٔ تغییر سفارش را ندارید."));
      if (hasPendingCatalogOrder()) {
        throw new Error(t("نتیجهٔ درخواست قبلی مشخص نیست؛ تلاش مجدد همان درخواست را بررسی می‌کند."));
      }
      const options = await catalogDraftOptions();
      if (!mounted.current || session !== agencySessionKey()) return;
      setDrafts(options);
      setLatest(options.find(function (option) { return option.id === selectedCatalogOrder(); }) || options[0] || null);
      setMode(selectedCatalogOrderTitle() || !options.length ? "new" : "latest");
      setTitle(selectedCatalogOrderTitle() || "");
      setSalesAgencyId(selectedCatalogAgency());
      setRetry(false);
      setRequest({ kind: "select", session });
    } catch (error) {
      AppFeedback.httpError(error);
    } finally {
      locked.current = false;
      if (mounted.current) setPending(false);
    }
  }

  return {
    add,
    changeOrder,
    changingOrder: pending && activeVariant === undefined,
    hasDestination: Boolean(selectedCatalogOrder() || selectedCatalogOrderTitle()),
    destinationTitle: selectedCatalogOrderTitle(),
    order: selectedCatalogOrder() === order?.id ? order : null,
    readFailed,
    activeVariant,
    reload: async function () {
      if (locked.current) return;
      locked.current = true;
      setPending(true);
      try { await refreshOrder(); }
      finally { locked.current = false; if (mounted.current) setPending(false); }
    },
    orderId: selectedCatalogOrder(),
    pending: pending || Boolean(request),
    dialog: (
      <StoreOrderDialog
        open={Boolean(request)}
        pending={pending}
        retry={retry}
        selectionOnly={request?.kind === "select"}
        latestLabel={latest ? latest.title || latest.orderNumber : undefined}
        drafts={drafts}
        selectedId={latest?.id}
        onSelect={function (id) {
          setLatest(drafts.find(function (draft) { return draft.id === id; }) || null);
        }}
        mode={mode}
        title={title}
        onMode={setMode}
        onTitle={setTitle}
        salesAgencyId={salesAgencyId}
        onAgencyChange={setSalesAgencyId}
        onSubmit={submit}
        onClose={function () { if (!locked.current) setRequest(undefined); }}
      />
    ),
  };
}
